import { ReverCard, ReverLoadingModal } from '@itsrever/design-system'
import {
  HorizontalBarChart,
  SimpleLineChart
} from '@/ui/components/analytics-components'
import { AnalyticsFilterBar } from '@/ui/components/analytics-filters'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useMemo } from 'react'
import moment from 'moment'
import { ReturnRateGraph } from '@/ui/components/analytics-components/product-analytics/return-rate-graph'
import {
  useProductReturnReasonsShown,
  useReturnRateAnalyticsAllowed
} from '@/hooks/use-scope-platform'
import { ProductProcessesTable } from '@/ui/components/product-details-components'
import { usePortalTranslate } from '@/hooks'
import {
  useCountries,
  useProductCountryReturnRate,
  useProductDetails,
  useProductReturnRate
} from '@/application/queries'
import { NoData } from '../../shared'
import { DateRangePicker, useAnalyticsDateRange } from '@/ui/shared/date-range'
import { useFilter } from '@/application/use-filter'
import { toCountryCodes, toCountryName } from '@/domain/countries'
import { withOtherCountries } from '@/domain/returns'

function ProductDetails() {
  const { trans } = usePortalTranslate({})

  const showProductReturnReasons = useProductReturnReasonsShown()

  const returnRateAnalyticsAllowed = useReturnRateAnalyticsAllowed()

  const { t } = useTranslation()
  const { id } = useParams()

  const { fromDate, toDate, range, setRange } = useAnalyticsDateRange()

  const { countries: countriesData, loadingCountries } = useCountries()
  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()

  const countryCodes = toCountryCodes(selectedCountries, countriesData)

  const { productDetails, totalReturnedCount, loading } = useProductDetails(
    id,
    fromDate,
    toDate,
    countriesData.length > 0 ? countryCodes : undefined
  )

  // Six months including the in-progress one, memoized to keep the range
  // stable across renders
  const startDate = useMemo(
    () => moment().subtract(5, 'months').startOf('month').format('YYYY-MM-DD'),
    []
  )
  const endDate = useMemo(
    () => moment().endOf('month').format('YYYY-MM-DD'),
    []
  )

  const { productReturnRate, loadingProductReturnRate } = useProductReturnRate(
    returnRateAnalyticsAllowed,
    id,
    startDate,
    endDate,
    countriesData.length > 0 ? countryCodes : undefined
  )

  const { productCountryReturnRate, loadingProductCountryReturnRate } =
    useProductCountryReturnRate(
      returnRateAnalyticsAllowed,
      id,
      fromDate,
      toDate,
      countriesData.length > 0 ? countryCodes : undefined
    )

  const countryReturnRates = useMemo(
    () =>
      withOtherCountries(
        productCountryReturnRate ?? [],
        t('product_details_page.other_countries')
      ),
    [productCountryReturnRate, t]
  )

  const currentMonthIndex = useMemo(() => {
    const currentMonth = moment().format('MMM')
    const index =
      productReturnRate?.findIndex((r) => r.period === currentMonth) ?? -1
    return index === -1 ? undefined : index
  }, [productReturnRate])

  const loadingPage =
    loading ||
    loadingProductReturnRate ||
    loadingProductCountryReturnRate ||
    loadingCountries

  return (
    <div className="flex h-full flex-col items-center overflow-scroll px-8 py-4 [&::-webkit-scrollbar]:hidden">
      <div className="w-full max-w-[1000px]">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h6>{productDetails?.product.name ?? ''}</h6>
              <p className="mt-2">
                {'ID: ' + id}
                {productDetails?.product.reference
                  ? ' / ' + productDetails.product.reference
                  : ''}
              </p>
            </div>
            <AnalyticsFilterBar
              startDate={fromDate}
              endDate={toDate}
              exclude={['collections']}
            />
          </div>
          <div className="w-60">
            <DateRangePicker selected={range} onSelect={setRange} />
          </div>
        </div>
        {loadingPage ? (
          <ReverLoadingModal loading />
        ) : (
          <>
            {showProductReturnReasons ? (
              <ReverCard className="mb-4 flex gap-8 p-4" fullWidth>
                {productDetails ? (
                  <>
                    <div
                      className="size-[225px] rounded-lg border border-solid border-neutral-300 bg-cover bg-center bg-no-repeat"
                      style={{
                        backgroundImage: `url(${productDetails.product.imageURL})`
                      }}
                    />
                    <div className="w-[700px]">
                      <p>{t('product_details_page.reasons_title')}</p>
                      <HorizontalBarChart
                        labels={productDetails.reasons.map((r) =>
                          trans(`select_reason_${r.key}`)
                        )}
                        values={productDetails.reasons.map(
                          (r) => r.totalReturnedItems
                        )}
                        total={totalReturnedCount}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex w-full flex-col items-center justify-center">
                    <p className="text-sm">
                      It looks like this product has no returns in the selected
                      period!
                    </p>
                    <NoData />
                  </div>
                )}
              </ReverCard>
            ) : null}
            <div className="grid h-[300px] grid-cols-2 gap-4">
              {productDetails ? (
                <SimpleLineChart
                  returns={productDetails.product?.totalReturns}
                  returnsByDay={productDetails.dailyReturns.map(
                    (rbd) => rbd.totalReturns
                  )}
                  dates={productDetails.dailyReturns.map((rbd) => rbd.period)}
                />
              ) : (
                <ReverCard fullWidth className="p-4">
                  <NoData />
                </ReverCard>
              )}
              <ReverCard fullWidth className="overflow-y-auto p-4">
                <p>{t('product_details_page.variant_breakdown_title')}</p>
                {productDetails ? (
                  <HorizontalBarChart
                    labels={productDetails.variantBreakdown.map(
                      (vb) => vb.name
                    )}
                    values={productDetails.variantBreakdown.map(
                      (vb) => vb.totalReturns
                    )}
                    total={totalReturnedCount}
                  />
                ) : (
                  <NoData />
                )}
              </ReverCard>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {returnRateAnalyticsAllowed && productReturnRate ? (
                <ReverCard className="px-4 pb-6 pt-4" fullWidth>
                  <ReturnRateGraph
                    title={t('product_details_page.return_rate_graph_title')}
                    itemsPurchased={productReturnRate.map((r) =>
                      Math.trunc(r.itemsPurchased)
                    )}
                    itemsReturned={productReturnRate.map((r) =>
                      Math.trunc(r.itemsReturned)
                    )}
                    returnRates={productReturnRate.map((r) =>
                      Math.round(r.returnRate)
                    )}
                    names={productReturnRate.map((r) => r.period)}
                    height={300}
                    inProgressIndex={currentMonthIndex}
                    inProgressNote={
                      currentMonthIndex !== undefined
                        ? t('product_details_page.current_month_incomplete')
                        : undefined
                    }
                    inProgressTooltip={t(
                      'product_details_page.current_month_in_progress'
                    )}
                  />
                </ReverCard>
              ) : null}
              {returnRateAnalyticsAllowed && productCountryReturnRate ? (
                <ReverCard className="px-4 pb-6 pt-4" fullWidth>
                  <ReturnRateGraph
                    title={t('product_details_page.country_return_rate_title')}
                    itemsPurchased={countryReturnRates.map(
                      (r) => r.itemsPurchased
                    )}
                    itemsReturned={countryReturnRates.map(
                      (r) => r.itemsReturned
                    )}
                    returnRates={countryReturnRates.map((r) =>
                      Math.round(r.returnRate)
                    )}
                    names={countryReturnRates.map((r) =>
                      toCountryName(r.country, countriesData)
                    )}
                    height={300}
                    rotateLabels
                  />
                </ReverCard>
              ) : null}
            </div>
            <div className="mt-4">
              <ReverCard fullWidth className="h-fit p-4">
                <p>{t('product_details_page.orders_with_variant')}</p>
                {productDetails ? (
                  <ProductProcessesTable
                    processes={productDetails.productProcesses}
                  />
                ) : (
                  <NoData />
                )}
              </ReverCard>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ProductDetails
